// shared/services/table-context-menu.service.ts
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ContextMenuAction } from '../components/base-material-table/base-material-table.component';
import { StaticSegmentsService } from '../../utils/static-segment.service';

@Injectable({
  providedIn: 'root'
})
export class TableContextMenuService {
  constructor(
    private router: Router,
    private snack: MatSnackBar,
    private staticSegmentsService: StaticSegmentsService
  ) {}

  /**
   * Standard Context Menu Actions für Entity-Listen
   */
  getStandardActions<T extends Record<string, any>>(config: {
    entityType: 'ansprechpartner' | 'filialen' | 'firmen' | 'konditionsverwaltung' | 'medien' | 'produkte' | 'gebiete';
    route?: any;
    idField?: string; // ← NEU: Welches Feld soll als ID verwendet werden
    showDelete?: boolean;
    canDelete?: (row: T) => boolean;
    onDelete?: (row: T) => void;
    showSplitView?: boolean;
    onSplitView?: (row: T) => void;
  }): ContextMenuAction<T>[] {
    const idField = config.idField || 'id'; // Default: 'id'

    const actions: ContextMenuAction<T>[] = [
      {
        label: 'Link in neuem Tab öffnen',
        icon: 'external-link',
        action: (row) => this.openInNewTab(config.entityType, row[idField], config.route)
      }
    ];

    // Split View Action (optional)
    if (config.showSplitView && config.onSplitView) {
      actions.push({
        label: 'Link in geteilter Ansicht öffnen',
        icon: 'columns',
        action: config.onSplitView
      });
    }

    actions.push(
      {
        label: 'Link in neuem Fenster öffnen',
        icon: 'window-restore',
        action: (row) => this.openInNewWindow(config.entityType, row[idField], config.route),
        divider: true
      },
      {
        label: 'Adresse des Link kopieren',
        icon: 'copy',
        action: (row) => this.copyLink(config.entityType, row[idField], row['name'], config.route)
      }
    );

    if (config.showDelete) {
      actions.push({
        label: 'Löschen',
        icon: 'trash-can',
        action: config.onDelete || (() => {}),
        visible: config.canDelete,
        divider: false
      });
    }

    return actions;
  }

  private openInNewTab(entityType: string, id: any, route?: any): void {
    const prefix = this.staticSegmentsService.getPrefixToSegment(entityType);
    const queryParams = route?.snapshot?.queryParams || {};
    const url = this.router.createUrlTree([...prefix, id], { queryParams }).toString();
    window.open(url, '_blank');
  }

  private openInNewWindow(entityType: string, id: any, route?: any): void {
    const prefix = this.staticSegmentsService.getPrefixToSegment(entityType);
    const queryParams = route?.snapshot?.queryParams || {};
    const url = this.router.createUrlTree([...prefix, id], { queryParams }).toString();

    const width = 1200;
    const height = 800;
    const left = (screen.width - width) / 2;
    const top = (screen.height - height) / 2;

    window.open(
      url,
      '_blank',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
  }

  copyLink(entityType: string, id: any, name?: string, route?: any): void {
    const prefix = this.staticSegmentsService.getPrefixToSegment(entityType);
    const queryParams = route?.snapshot?.queryParams || {};
    const relativeUrl = this.router.createUrlTree([...prefix, id], { queryParams }).toString();
    const fullUrl = window.location.origin + relativeUrl;
    const entityName = name ? ` von ${name}` : '';

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(fullUrl).then(() => {
        this.snack.open(`Link${entityName} wurde in die Zwischenablage kopiert`, undefined, {
          duration: 3000
        });
      }).catch(err => {
        console.error('Fehler beim Kopieren:', err);
        this.showCopyError();
      });
    } else {
      this.fallbackCopyToClipboard(fullUrl, entityName);
    }
  }

  private fallbackCopyToClipboard(text: string, entityName: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
      this.snack.open(`Link${entityName} wurde in die Zwischenablage kopiert`, undefined, {
        duration: 3000
      });
    } catch (err) {
      console.error('Fehler beim Kopieren:', err);
      this.showCopyError();
    } finally {
      document.body.removeChild(textArea);
    }
  }

  private showCopyError(): void {
    this.snack.open('Fehler beim Kopieren des Links', undefined, {
      panelClass: 'error',
      duration: 3000
    });
  }
}
